import React, { useState } from 'react';
import { FaNetworkWired, FaTimes, FaCube, FaFileInvoice, FaCalendarAlt, FaPlug, FaChartBar, FaMapMarkerAlt, FaHardHat, FaMap, FaPrint, FaDownload, FaCircle } from 'react-icons/fa';
import { KavachSwitch, ModalTab } from '../utils/kavachTypes';
import { exportSwitchDetailToPdf } from '../utils/kavachPdfExport';
import { toast } from 'react-hot-toast';

interface KavachAssetModalProps {
  isOpen: boolean;
  switchData: KavachSwitch | null;
  onClose: () => void;
}

const KavachAssetModal: React.FC<KavachAssetModalProps> = ({ isOpen, switchData, onClose }) => {
  const [activeTab, setActiveTab] = useState<ModalTab>('asset-details');

  if (!switchData) return null;

  const { assetDetails, ports } = switchData;

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    if (!switchData) return;
    try {
      exportSwitchDetailToPdf(switchData);
      toast.success('PDF exported successfully');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('Failed to export PDF');
    }
  };

  const tabs: { id: ModalTab; label: string }[] = [
    { id: 'asset-details', label: 'Asset Details' },
    { id: 'network-info', label: 'Network Information' },
    { id: 'location-info', label: 'Location & Installation' },
  ];

  return (
    <div className={`modal-overlay ${isOpen ? 'active' : ''}`} onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <FaNetworkWired />
            <span>{switchData.name}</span>
          </div>
          <button className="close-modal" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="modal-body">
          <div className="asset-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`asset-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Asset Details Tab */}
          <div className={`asset-tab-content ${activeTab === 'asset-details' ? 'active' : ''}`}>
            <div className="info-grid">
              <div className="info-section">
                <h4><FaCube /> Basic Information</h4>
                <div className="info-item">
                  <div className="info-label">Make:</div>
                  <div className="info-value">{assetDetails.make}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Model:</div>
                  <div className="info-value">{assetDetails.model}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Serial Number:</div>
                  <div className="info-value highlight">{assetDetails.serialNumber}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Manufacture Number:</div>
                  <div className="info-value">{assetDetails.manufactureNumber}</div>
                </div>
              </div>

              <div className="info-section">
                <h4><FaFileInvoice /> Purchase & Warranty</h4>
                <div className="info-item">
                  <div className="info-label">Purchase Order No:</div>
                  <div className="info-value">{assetDetails.purchaseOrderNo}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Purchase Date:</div>
                  <div className="info-value">{assetDetails.purchaseDate}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Installation Date:</div>
                  <div className="info-value">{assetDetails.installationDate}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Warranty Expiry:</div>
                  <div className="info-value">{assetDetails.warrantyExpiry}</div>
                </div>
              </div>
            </div>

            <div className="info-section">
              <h4><FaCalendarAlt /> Lifecycle Information</h4>
              <div className="info-grid">
                <div className="info-item">
                  <div className="info-label">Codal Life:</div>
                  <div className="info-value">{assetDetails.codalLife}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Remaining Life:</div>
                  <div className="info-value success">{assetDetails.remainingLife}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Installation Age:</div>
                  <div className="info-value">{assetDetails.installationAge}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Warranty Status:</div>
                  <div className={`info-value ${assetDetails.warrantyStatus === 'Active' ? 'success' : 'danger'}`}>
                    {assetDetails.warrantyStatus}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Network Information Tab */}
          <div className={`asset-tab-content ${activeTab === 'network-info' ? 'active' : ''}`}>
            <div className="info-grid">
              <div className="info-section">
                <h4><FaNetworkWired /> Network Configuration</h4>
                <div className="info-item">
                  <div className="info-label">Connected To:</div>
                  <div className="info-value">{assetDetails.connectedTo}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">IP Address:</div>
                  <div className="info-value highlight">{assetDetails.ipAddress}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">MAC Address:</div>
                  <div className="info-value">{assetDetails.macAddress}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Firmware Version:</div>
                  <div className="info-value">{assetDetails.firmwareVersion}</div>
                </div>
              </div>

              <div className="info-section">
                <h4><FaChartBar /> Performance Metrics</h4>
                <div className="info-item">
                  <div className="info-label">Uptime:</div>
                  <div className="info-value success">{assetDetails.uptime}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Average Latency:</div>
                  <div className="info-value">{assetDetails.avgLatency}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Packet Loss:</div>
                  <div className="info-value">{assetDetails.packetLoss}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Last Reboot:</div>
                  <div className="info-value">{assetDetails.lastReboot}</div>
                </div>
              </div>
            </div>

            <div className="info-section">
              <h4><FaPlug /> Port Status</h4>
              <div className="status-indicators">
                <div className={`status-indicator-item ${ports.port1.status.toLowerCase()}`}>
                  <FaCircle />
                  <span>Port 1: {ports.port1.status} ({ports.port1.speed})</span>
                </div>
                <div className={`status-indicator-item ${ports.port2.status.toLowerCase()}`}>
                  <FaCircle />
                  <span>Port 2: {ports.port2.status} ({ports.port2.speed})</span>
                </div>
                <div className={`status-indicator-item ${ports.port3.status.toLowerCase()}`}>
                  <FaCircle />
                  <span>Port 3: {ports.port3.status} ({ports.port3.speed})</span>
                </div>
                <div className={`status-indicator-item ${ports.port4.status.toLowerCase()}`}>
                  <FaCircle />
                  <span>Port 4: {ports.port4.status} ({ports.port4.speed})</span>
                </div>
              </div>
            </div>
          </div>

          {/* Location & Installation Tab */}
          <div className={`asset-tab-content ${activeTab === 'location-info' ? 'active' : ''}`}>
            <div className="info-grid">
              <div className="info-section">
                <h4><FaMapMarkerAlt /> Location Details</h4>
                <div className="info-item">
                  <div className="info-label">Latitude:</div>
                  <div className="info-value">{assetDetails.latitude}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Longitude:</div>
                  <div className="info-value">{assetDetails.longitude}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Asset Position:</div>
                  <div className="info-value">{assetDetails.assetPosition}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Platform No:</div>
                  <div className="info-value">{assetDetails.platformNo}</div>
                </div>
              </div>

              <div className="info-section">
                <h4><FaHardHat /> Installation Details</h4>
                <div className="info-item">
                  <div className="info-label">Installation Team:</div>
                  <div className="info-value">{assetDetails.installationTeam}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Installation Supervisor:</div>
                  <div className="info-value">{assetDetails.supervisor}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Last Maintenance:</div>
                  <div className="info-value">{assetDetails.lastMaintenance}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Next Maintenance Due:</div>
                  <div className="info-value warning">{assetDetails.nextMaintenanceDue}</div>
                </div>
              </div>
            </div>

            <div className="info-section">
              <h4><FaMap /> Location Map</h4>
              <div className="map-container">
                <div className="map-placeholder">
                  <FaMapMarkerAlt className="map-icon" />
                  <p>Location: {switchData.station} Station</p>
                  <p>Coordinates: {assetDetails.latitude}, {assetDetails.longitude}</p>
                  <p className="small">Map integration available with GPS coordinates</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="modal-btn secondary" onClick={handlePrint}>
            <FaPrint />
            Print Details
          </button>
          <button className="modal-btn primary" onClick={handleExport}>
            <FaDownload />
            Export as PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default KavachAssetModal;
